import React, { useEffect } from 'react';
import {
    Card,
    Form,
    Input,
    InputNumber,
    DatePicker,
    Select,
    Divider,
    Row,
    Col,
    Space,
    Tag,
    Typography
} from '@douyinfe/semi-ui';
import { IconUser, IconCalendar } from '@douyinfe/semi-icons';

const { Text } = Typography;

interface ReleaseFormProps {
    leadInfo: any;
    onFormApiReady: (formApi: any) => void;
    disabled?: boolean;
}

const ReleaseForm: React.FC<ReleaseFormProps> = ({
    leadInfo,
    onFormApiReady,
    disabled = false
}) => {
    const [formApi, setFormApi] = React.useState<any>(null);
    
    useEffect(() => {
        if (formApi && leadInfo) {
            // 设置表单初始值
            formApi.setValues({
                leadId: leadInfo.id,
                name: leadInfo.name,
                age: leadInfo.age ,
                marriage: leadInfo.marriage ,
                company: leadInfo.company ,
                companyName: leadInfo.companyName ,
                applyAmount: leadInfo.applyAmount ,
                loanPoint: leadInfo.loanPoint ,
                salaryBankName: leadInfo.salaryBankName ,
                salary: leadInfo.salary ,
                interest: leadInfo.interest ,
                borrowInterest: leadInfo.borrowInterest ,
                postingInterest: leadInfo.postingInterest ,
                commission: leadInfo.commission,
                settledLoanAt: leadInfo.settledLoanAt || new Date(),
                settledLoanOperator: leadInfo.settledLoanOperator || ''
            });
        }
    }, [formApi, leadInfo]);
    
    // 获取贷款类型选项
    const getLoanTypeOptions = () => [
        { label: '信贷', value: 'MORTGAGE' },
        { label: '信用卡', value: 'CARD' },
        { label: '借款', value: 'BORROW' }
    ];
    
    // 获取婚姻状态选项
    const getMarriageOptions = () => [
        { label: '已婚', value: '已婚' },
        { label: '未婚', value: '未婚' },
        { label: '离异', value: '离异' }
    ];
    
    // 获取单位属性选项
    const getCompanyOptions = () => [
        { label: '无公司', value: '无公司' },
        { label: '普通单位', value: '普通单位' },
        { label: '事业单位', value: '事业单位' },
        { label: '国/央企', value: '国/央企' },
        { label: '个体户', value: '个体户' },
        { label: '有限公司', value: '有限公司' }
    ];
    
    return (
        <Card
            title="客户信息"
            headerExtraContent={
                disabled ? (
                    <Tag color="green" size="small">
                        已释放
                    </Tag>
                ) : null
            }
        >
            <Form
                getFormApi={api => {
                    setFormApi(api);
                    onFormApiReady(api);
                }}
                labelPosition="left"
                labelWidth="100px"
                disabled={disabled}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Input
                            label="客户ID"
                            field="leadId"
                            initValue={leadInfo?.id}
                            disabled
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Input
                            label="客户姓名"
                            field="name"
                            prefix={<IconUser />}
                            disabled
                        />
                    </Col>
                </Row>
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.InputNumber
                            label="年龄"
                            field="age"
                            initValue={leadInfo?.age}
                            min={18}
                            max={100}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Select
                            label="婚姻状态"
                            field="marriage"
                            initValue={leadInfo?.marriage}
                            optionList={getMarriageOptions()}
                        />
                    </Col>
                </Row>
                
                <Divider />
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.InputNumber
                            label="签约金额"
                            field="applyAmount"
                            initValue={leadInfo?.applyAmount}
                            disabled
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Input
                            label="点位"
                            field="loanPoint"
                            initValue={leadInfo?.loanPoint}
                        />
                    </Col>
                </Row>
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.InputNumber
                            label="日利率"
                            field="interest"
                            step={0.0001}
                            min={0}
                            max={0.01}
                            rules={[{ required: true, message: "请填写" }]}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.InputNumber
                            label="抽佣"
                            field="commission"
                            step={0.01}
                            min={0}
                            max={1}
                            rules={[{ required: true, message: "请填写" }]}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.InputNumber
                            label="借款日利率"
                            field="borrowInterest"
                            step={0.0001}
                            min={0}
                            max={0.01}
                            rules={[{ required: true, message: "请填写" }]}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.InputNumber
                            label="过账利率"
                            field="postingInterest"
                            step={0.01}
                            min={0}
                            max={1}
                            rules={[{ required: true, message: "请填写" }]}
                        />
                    </Col>
                </Row>
                
                <Divider />
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Select
                            label="单位属性"
                            field="company"
                            initValue={leadInfo?.company}
                            optionList={getCompanyOptions()}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Input
                            label="单位名称"
                            field="companyName"
                            initValue={leadInfo?.companyName}
                        />
                    </Col>
                </Row>
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Input
                            label="代发银行"
                            field="salaryBankName"
                            initValue={leadInfo?.salaryBankName}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Input
                            label="代发工资"
                            field="salary"
                            initValue={leadInfo?.salary}
                        />
                    </Col>
                </Row>
                
                <Divider />
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.DatePicker
                            label="释放日期"
                            field="settledLoanAt"
                            prefix={<IconCalendar />}
                            type="date"
                            disabledDate={date => date > new Date()}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Input
                            label="经办人"
                            field="settledLoanOperator"
                            placeholder="请输入经办人姓名"
                        />
                    </Col>
                </Row>
                
                {leadInfo?.agentName && (
                    <Space>
                        <Text type="secondary">按揭专员：</Text>
                        <Tag color="blue">{leadInfo.agentName}</Tag>
                    </Space>
                )}
                
                {leadInfo?.saleName && (
                    <Space style={{ marginLeft: 16 }}>
                        <Text type="secondary">销售：</Text>
                        <Tag color="green">{leadInfo.saleName}</Tag>
                    </Space>
                )}
            </Form>
        </Card>
    );
};

export default ReleaseForm;